import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import SummarizeOutlined from "@mui/icons-material/SummarizeOutlined";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { DownloadFormatButton } from "../../../components/UIToolkit/Clickable/DownloadFormatButton";
import type { SandboxRenderResult } from "../../../components/UIToolkit/Previews/SandboxedHtmlRenderer";
import { FONT_SIZE, INK } from "../../../theme/tokens";

type Props = {
	report: SandboxRenderResult;
	/** A template is on screen (not still loading). */
	hasSource: boolean;
	/** How many people work the day — shown once the check has passed. */
	peopleCount: number;
	/** A summary the user confirmed: it doesn't list each person, so the line says that instead of the count. */
	isSummary: boolean;
	onPrint: () => void;
};

/** Why Print is off, when it is. */
const printBlockedKey = (report: SandboxRenderResult, hasSource: boolean): string => {
	if (!hasSource || report.status === "rendering") return "printout.custom.ai.print.rendering";
	return report.status === "failed" ? "printout.custom.ai.print.failed" : "printout.custom.ai.print.blocked";
};

/**
 * Print — on only while everyone working the day is visibly on the page, or a summary draws — with the check's verdict
 * beside it.
 */
export const CustomPrintoutPrintBar: FC<Props> = ({ report, hasSource, peopleCount, isSummary, onPrint }) => {
	const { t } = useTranslation();
	const canPrint = hasSource && report.status === "rendered" && report.report.ok;
	return (
		<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
			{canPrint && isSummary && (
				<Typography
					data-testid="custom-printout-summary-note"
					sx={{ marginInlineEnd: "auto", display: "flex", alignItems: "center", gap: 0.75, fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}
				>
					<SummarizeOutlined sx={{ fontSize: FONT_SIZE.TITLE_MD }} aria-hidden />
					{t("printout.custom.ai.summary.note")}
				</Typography>
			)}
			{canPrint && !isSummary && peopleCount > 0 && (
				<Typography
					data-testid="custom-printout-complete"
					sx={{ marginInlineEnd: "auto", display: "flex", alignItems: "center", gap: 0.75, fontSize: FONT_SIZE.SECONDARY, color: "success.dark" }}
				>
					<CheckCircleOutlined sx={{ fontSize: FONT_SIZE.TITLE_MD }} aria-hidden />
					{t("printout.custom.ai.complete", { count: peopleCount })}
				</Typography>
			)}
			<DownloadFormatButton
				dataTestIdPrefix="custom-printout"
				formats={[]}
				onPrint={onPrint}
				disabled={!canPrint}
				disabledReason={t(printBlockedKey(report, hasSource))}
			/>
		</Box>
	);
};
